/* Copyright (c) 2026 Dobler Data Solutions, LLC. All rights reserved. */

import { useEffect, useRef, useState } from 'react';
import type { CoverageDto, ProgressDataPoint, ProductivityDto, AuditEntryDto, ActiveClaimDto } from '@shared/types';
import { fetchCoverage, fetchProgress, fetchProductivity, fetchAuditTrail, fetchActiveClaims, forceReleaseClaim } from '../services/formularyApi';
import { PageHelp } from '../components/layout/PageHelp';
import { Tooltip } from '../components/layout/Tooltip';
import { useAuthStore } from '../stores/authStore';
import { trackEvent } from '../services/telemetry';

export function DashboardPage() {
  const [coverage, setCoverage] = useState<CoverageDto | null>(null);
  const [progress, setProgress] = useState<ProgressDataPoint[]>([]);
  const [productivity, setProductivity] = useState<ProductivityDto[]>([]);
  const [audit, setAudit] = useState<AuditEntryDto[]>([]);
  const [activeClaims, setActiveClaims] = useState<ActiveClaimDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [days, setDays] = useState(30);
  const hasTrackedRef = useRef(false);
  const userRole = useAuthStore((s) => s.user?.role ?? 'unknown');

  useEffect(() => {
    if (!hasTrackedRef.current) {
      hasTrackedRef.current = true;
      trackEvent('dashboard_viewed', { userRole, dayRange: days });
    }
    async function load() {
      setIsLoading(true);
      setError(null);
      // Load each section independently — partial failures don't block the page
      const results = await Promise.allSettled([
        fetchCoverage(),
        fetchProgress(days),
        fetchProductivity(days),
        fetchAuditTrail({ page: 1, pageSize: 25 }),
        fetchActiveClaims(),
      ]);
      if (results[0].status === 'fulfilled') setCoverage(results[0].value.data);
      if (results[1].status === 'fulfilled') setProgress(results[1].value.data);
      if (results[2].status === 'fulfilled') setProductivity(results[2].value.data);
      if (results[3].status === 'fulfilled') setAudit(results[3].value.data);
      if (results[4].status === 'fulfilled') setActiveClaims(results[4].value.data);
      const failures = results.filter((r) => r.status === 'rejected');
      if (failures.length === results.length) {
        setError('Failed to load dashboard data');
      }
      setIsLoading(false);
    }
    load();
  }, [days]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-3 border-dds-blue-500 border-t-transparent rounded-full animate-spin" />
          <span className="text-sm text-gray-400">Loading dashboard...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 m-4">
        <strong>Error:</strong> {error}
      </div>
    );
  }

  const pct = coverage?.percentComplete ?? 0;

  return (
    <div className="space-y-5">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Dashboard</h2>
          <p className="text-sm text-gray-500 mt-0.5">Taxonomy assignment coverage &amp; team activity</p>
        </div>
        <div className="flex items-center gap-2">
          <Tooltip text="Select the time window for activity charts and productivity metrics">
            <span className="text-xs text-gray-400 cursor-help">Period:</span>
          </Tooltip>
          {[7, 14, 30, 90].map((d) => (
            <button
              key={d}
              data-testid={`period-btn-${d}d`}
              onClick={() => setDays(d)}
              className={`px-2.5 py-1 text-xs rounded-md font-medium transition-colors ${
                days === d
                  ? 'bg-dds-blue-500 text-white'
                  : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      <PageHelp
        title="How to read the Dashboard"
        steps={[
          'The hero cards show overall coverage — how many formulas have taxonomy codes assigned.',
          'Use the period selector (7d / 14d / 30d / 90d) to change the time window for charts.',
          'The Activity chart shows daily assignment volume over the selected period.',
          'The Team Productivity leaderboard ranks pharmacists by total actions.',
          'Scroll down to Recent Activity for a detailed audit trail.',
        ]}
        tips={[
          'Coverage color: green (≥75%), amber (50–75%), red (<50%).',
          'If sections show "No data", the period may be too short — try 30 or 90 days.',
          'Dashboard is only visible to Manager and Admin roles.',
        ]}
      />

      {/* ─── Coverage hero cards ─── */}
      {coverage && (
        <div className="grid grid-cols-4 gap-4">
          <HeroCard
            label="Total Formulas"
            value={coverage.totalFormulas.toLocaleString()}
            icon="📋"
            bg="bg-gray-50"
            accent="border-gray-200"
            tooltip="Total number of compounding formulas in the system"
            testId="coverage-total"
          />
          <HeroCard
            label="Assigned"
            value={coverage.assignedCount.toLocaleString()}
            icon="✅"
            bg="bg-green-50"
            accent="border-green-200"
            sub={`${coverage.totalFormulas > 0 ? Math.round((coverage.assignedCount / coverage.totalFormulas) * 100) : 0}% of total`}
            tooltip="Formulas with at least one taxonomy code assigned"
          />
          <HeroCard
            label="Unassigned"
            value={coverage.unassignedCount.toLocaleString()}
            icon="⏳"
            bg="bg-amber-50"
            accent="border-amber-200"
            sub={`${coverage.totalFormulas > 0 ? Math.round((coverage.unassignedCount / coverage.totalFormulas) * 100) : 0}% remaining`}
            tooltip="Formulas waiting for taxonomy assignment"
          />
          <div className={`rounded-xl border-2 border-dds-blue-200 bg-gradient-to-br from-dds-blue-50 to-white p-4 flex flex-col items-center justify-center`}>
            <span className="text-xs text-dds-blue-400 font-medium uppercase tracking-wider mb-1">Coverage</span>
            <div className="relative w-20 h-20">
              <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90">
                <circle cx="18" cy="18" r="15.9" fill="none" stroke="#E5E7EB" strokeWidth="3" />
                <circle
                  cx="18" cy="18" r="15.9" fill="none"
                  stroke={pct >= 75 ? '#10B981' : pct >= 50 ? '#F59E0B' : '#EF4444'}
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeDasharray={`${pct} ${100 - pct}`}
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-lg font-bold text-gray-900">{pct}%</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── Active Claims ─── */}
      <ActiveClaimsPanel claims={activeClaims} onForceRelease={async (batchId) => {
        await forceReleaseClaim(batchId);
        trackEvent('formula_claim_released', { releaseType: 'forced' });
        const refreshed = await fetchActiveClaims();
        setActiveClaims(refreshed.data);
      }} />

      {/* ─── Middle row: Progress chart + Productivity ─── */}
      <div className="grid grid-cols-5 gap-4">
        {/* Progress sparkline */}
        <div className="col-span-3 bg-white rounded-xl border border-gray-200 p-4">
          <h3 className="text-sm font-semibold text-gray-800 mb-3">Assignment Activity ({days} days)</h3>
          {progress.length === 0 ? (
            <div className="text-gray-400 text-sm py-8 text-center">No assignment activity in this period</div>
          ) : (
            <BarChart data={progress} />
          )}
        </div>

        {/* Productivity leaderboard */}
        <div className="col-span-2 bg-white rounded-xl border border-gray-200 p-4">
          <h3 className="text-sm font-semibold text-gray-800 mb-3">Team Productivity</h3>
          {productivity.length === 0 ? (
            <div className="text-gray-400 text-sm py-8 text-center">No activity yet</div>
          ) : (
            <div className="space-y-2.5">
              {productivity.map((p, idx) => {
                const total = p.assignmentCount + p.reassignmentCount + p.removalCount;
                const maxTotal = Math.max(...productivity.map((x) => x.assignmentCount + x.reassignmentCount + x.removalCount));
                const pctBar = maxTotal > 0 ? (total / maxTotal) * 100 : 0;
                return (
                  <div key={p.pharmacistOid}>
                    <div className="flex items-center justify-between mb-0.5">
                      <div className="flex items-center gap-2">
                        <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold text-white ${
                          idx === 0 ? 'bg-dds-blue-500' : idx === 1 ? 'bg-dds-blue-400' : 'bg-gray-400'
                        }`}>{idx + 1}</span>
                        <span className="text-sm text-gray-800 font-medium truncate max-w-[140px]">{p.pharmacistName}</span>
                      </div>
                      <div className="flex items-center gap-3 text-[11px]">
                        <span className="text-green-600">{p.assignmentCount} <span className="text-gray-400">asn</span></span>
                        <span className="text-blue-600">{p.reassignmentCount} <span className="text-gray-400">rea</span></span>
                        <span className="text-red-500">{p.removalCount} <span className="text-gray-400">rem</span></span>
                        <span className="font-bold text-gray-900 w-8 text-right">{total}</span>
                      </div>
                    </div>
                    <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-dds-blue-400 to-dds-blue-500 rounded-full transition-all" style={{ width: `${pctBar}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* ─── Recent activity ─── */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <h3 className="text-sm font-semibold text-gray-800 mb-3">Recent Activity</h3>
        {audit.length === 0 ? (
          <div className="text-gray-400 text-sm py-6 text-center">No audit entries yet</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-gray-500 border-b border-gray-100">
                  <th className="text-left py-2 pr-4 font-medium">Time</th>
                  <th className="text-left py-2 pr-4 font-medium">User</th>
                  <th className="text-left py-2 pr-4 font-medium">Action</th>
                  <th className="text-left py-2 font-medium">Notes</th>
                </tr>
              </thead>
              <tbody>
                {audit.map((a, i) => (
                  <tr key={i} className="border-b border-gray-50 hover:bg-gray-50/50">
                    <td className="py-2 pr-4 text-gray-500 text-xs whitespace-nowrap">
                      {new Date(a.assignedAt).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </td>
                    <td className="py-2 pr-4 text-gray-800 font-medium text-xs">{a.assignedByName}</td>
                    <td className="py-2 pr-4">
                      <ActionBadge type={a.actionType} />
                    </td>
                    <td className="py-2 text-gray-500 text-xs truncate max-w-xs">{a.notes || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Sub-components ─────────────────────────────────────────────────────────

function HeroCard({ label, value, icon, bg, accent, sub, tooltip, testId }: {
  label: string; value: string; icon: string; bg: string; accent: string; sub?: string; tooltip?: string; testId?: string;
}) {
  return (
    <div className={`${bg} border ${accent} rounded-xl p-4`} data-testid={testId}>
      <div className="flex items-center gap-2 mb-2">
        <span className="text-lg">{icon}</span>
        {tooltip ? (
          <Tooltip text={tooltip}>
            <span className="text-xs text-gray-500 font-medium uppercase tracking-wider cursor-help">{label}</span>
          </Tooltip>
        ) : (
          <span className="text-xs text-gray-500 font-medium uppercase tracking-wider">{label}</span>
        )}
      </div>
      <div className="text-3xl font-bold text-gray-900">{value}</div>
      {sub && <div className="text-xs text-gray-400 mt-1">{sub}</div>}
    </div>
  );
}

function ActionBadge({ type }: { type: string }) {
  const config: Record<string, { bg: string; text: string; label: string }> = {
    ASSIGN:       { bg: 'bg-green-100', text: 'text-green-700', label: 'Assign' },
    BATCH_ASSIGN: { bg: 'bg-green-100', text: 'text-green-700', label: 'Batch' },
    REASSIGN:     { bg: 'bg-blue-100',  text: 'text-blue-700',  label: 'Reassign' },
    REMOVE:       { bg: 'bg-red-100',   text: 'text-red-700',   label: 'Remove' },
  };
  const c = config[type] ?? { bg: 'bg-gray-100', text: 'text-gray-600', label: type };
  return <span className={`${c.bg} ${c.text} px-2 py-0.5 rounded-full text-[11px] font-medium`}>{c.label}</span>;
}

function ActiveClaimsPanel({ claims, onForceRelease }: {
  claims: ActiveClaimDto[];
  onForceRelease: (batchId: string) => Promise<void>;
}) {
  const [releasing, setReleasing] = useState<string | null>(null);

  if (claims.length === 0) return null;

  const handleForceRelease = async (batchId: string) => {
    setReleasing(batchId);
    try {
      await onForceRelease(batchId);
    } finally {
      setReleasing(null);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <h3 className="text-sm font-semibold text-gray-800 mb-3">Active Claims ({claims.length})</h3>
      <div className="space-y-2">
        {claims.map((c) => {
          const expiresAt = new Date(c.expiresAt);
          const isExpired = expiresAt < new Date();
          const minutesLeft = Math.max(0, Math.round((expiresAt.getTime() - Date.now()) / 60_000));

          return (
            <div key={c.batchId} className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-dds-blue-100 flex items-center justify-center text-dds-blue-600 text-xs font-bold">
                  {c.claimedByName.charAt(0).toUpperCase()}
                </div>
                <div>
                  <div className="text-sm font-medium text-gray-800">{c.claimedByName}</div>
                  <div className="text-xs text-gray-500">{c.formulaCount} formulas</div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className={`text-xs ${isExpired ? 'text-red-500 font-medium' : 'text-gray-400'}`}>
                  {isExpired ? 'Expired' : `${minutesLeft}m left`}
                </span>
                <Tooltip text="Force-release this claim so the formulas become available to other users">
                  <button
                    onClick={() => handleForceRelease(c.batchId)}
                    disabled={releasing === c.batchId}
                    className="text-xs text-red-600 hover:text-red-800 underline disabled:opacity-50"
                  >
                    {releasing === c.batchId ? 'Releasing...' : 'Force Release'}
                  </button>
                </Tooltip>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** Simple bar chart using CSS — no external deps */
function BarChart({ data }: { data: ProgressDataPoint[] }) {
  const maxVal = Math.max(...data.map((d) => d.assignmentCount || 0), 1);
  // Show at most 30 bars, take the latest if more
  const bars = data.slice(-30);

  return (
    <div>
      <div className="flex items-end gap-[3px] h-28">
        {bars.map((d) => {
          const h = Math.max(((d.assignmentCount || 0) / maxVal) * 100, 2);
          return (
            <div key={d.date} className="flex-1 group relative flex flex-col items-center justify-end h-full">
              <div
                className="w-full bg-dds-blue-400 rounded-t transition-all group-hover:bg-dds-blue-500"
                style={{ height: `${h}%`, minHeight: 2 }}
              />
              <div className="absolute bottom-full mb-1 hidden group-hover:block bg-gray-800 text-white text-[10px] px-1.5 py-0.5 rounded whitespace-nowrap z-10">
                {d.date}: {d.assignmentCount || 0}
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex justify-between mt-1.5 text-[10px] text-gray-400">
        <span>{bars[0]?.date}</span>
        <span>{bars[bars.length - 1]?.date}</span>
      </div>
    </div>
  );
}
